import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const BASE = `${process.env.REACT_APP_REQUEST_URL}/api/v2/guest-list`

async function parseJson(res) {
	const data = await res.json().catch(() => ({}))
	if (!res.ok) {
		const err = new Error(data.message || 'Request failed')
		err.status = res.status
		err.code = data.code
		err.errors = data.errors
		err.candidates = data.candidates
		err.body = data
		throw err
	}
	return data
}

/**
 * Fetch every page of guests for CSV export without updating Redux list state.
 * Respects includeArchived + q (same as list); caller applies UI filters.
 */
export async function fetchAllGuestsForExport({
	kazooId,
	includeArchived = false,
	q = '',
}) {
	const pageSize = 200
	const all = []
	let page = 1
	let total = Infinity

	while ((page - 1) * pageSize < total) {
		const params = new URLSearchParams({
			page: String(page),
			pageSize: String(pageSize),
			includeArchived: includeArchived ? 'true' : 'false',
		})
		if (q) params.set('q', q)
		const res = await fetch(`${BASE}/${kazooId}/guests?${params}`, {
			credentials: 'include',
		})
		const data = await parseJson(res)
		const batch = data.guests ?? []
		all.push(...batch)
		total = data.total ?? batch.length
		if (batch.length === 0) break
		page += 1
	}

	return all
}

export const fetchGuestList = createAsyncThunk(
	'guestList/fetchGuestList',
	async ({ kazooId, page = 1, pageSize = 50, includeArchived = false, q = '' }) => {
		const safePageSize = Math.min(Math.max(1, Number(pageSize) || 50), 200)
		const params = new URLSearchParams({
			page: String(Math.max(1, Number(page) || 1)),
			pageSize: String(safePageSize),
			includeArchived: includeArchived ? 'true' : 'false',
		})
		if (q) params.set('q', q)
		const res = await fetch(`${BASE}/${kazooId}/guests?${params}`, {
			credentials: 'include',
		})
		return parseJson(res)
	}
)

export const searchGuestList = createAsyncThunk(
	'guestList/searchGuestList',
	async ({ kazooId, page = 1, pageSize = 50, includeArchived = false, filters = {}, sort }) => {
		const safePageSize = Math.min(Math.max(1, Number(pageSize) || 50), 200)
		const res = await fetch(`${BASE}/${kazooId}/guests/search`, {
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({
				page: Math.max(1, Number(page) || 1),
				pageSize: safePageSize,
				includeArchived,
				filters,
				sort,
			}),
		})
		return parseJson(res)
	}
)

export const createGuestListGuest = createAsyncThunk(
	'guestList/createGuest',
	async ({ kazooId, ...body }, { rejectWithValue }) => {
		try {
			const res = await fetch(`${BASE}/${kazooId}/guests`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify(body),
			})
			return await parseJson(res)
		} catch (err) {
			return rejectWithValue({
				message: err.message,
				code: err.code,
				errors: err.errors,
				candidates: err.candidates,
			})
		}
	}
)

export const updateGuestListGuest = createAsyncThunk(
	'guestList/updateGuest',
	async ({ kazooId, guestId, ...patch }) => {
		const res = await fetch(`${BASE}/${kazooId}/guests/${guestId}`, {
			method: 'PATCH',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(patch),
		})
		return parseJson(res)
	}
)

export const deleteGuestListGuest = createAsyncThunk(
	'guestList/deleteGuest',
	async ({
		kazooId,
		guestId,
		confirm = true,
		acknowledgeArchiveRecommended = true,
	}) => {
		const res = await fetch(`${BASE}/${kazooId}/guests/${guestId}`, {
			method: 'DELETE',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify({ confirm, acknowledgeArchiveRecommended }),
		})
		return parseJson(res)
	}
)

export const fetchGuestTags = createAsyncThunk(
	'guestList/fetchGuestTags',
	async (kazooId) => {
		const res = await fetch(`${BASE}/${kazooId}/guest-tags`, {
			credentials: 'include',
		})
		return parseJson(res)
	}
)

export const fetchEventTags = createAsyncThunk(
	'guestList/fetchEventTags',
	async (kazooId) => {
		const res = await fetch(`${BASE}/${kazooId}/event-tags`, {
			credentials: 'include',
		})
		return parseJson(res)
	}
)

export const patchEventTags = createAsyncThunk(
	'guestList/patchEventTags',
	async ({ kazooId, add, remove, rename }) => {
		const body = {}
		if (add?.length) body.add = add
		if (remove?.length) body.remove = remove
		if (rename) body.rename = rename
		const res = await fetch(`${BASE}/${kazooId}/event-tags`, {
			method: 'PATCH',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
			body: JSON.stringify(body),
		})
		return parseJson(res)
	}
)

export const fetchCustomFields = createAsyncThunk(
	'guestList/fetchCustomFields',
	async (kazooId) => {
		const res = await fetch(`${BASE}/${kazooId}/custom-fields`, {
			credentials: 'include',
		})
		return parseJson(res)
	}
)

/** M6 — paste/bulk import: preview never writes; confirm persists. */
export const pasteGuestListPreview = createAsyncThunk(
	'guestList/pastePreview',
	async ({ kazooId, rows }, { rejectWithValue }) => {
		try {
			const res = await fetch(`${BASE}/${kazooId}/guests/paste`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ mode: 'preview', rows }),
			})
			return await parseJson(res)
		} catch (err) {
			return rejectWithValue({
				message: err.message,
				code: err.code,
				errors: err.errors,
			})
		}
	}
)

export const pasteGuestListConfirm = createAsyncThunk(
	'guestList/pasteConfirm',
	async ({ kazooId, rows }, { rejectWithValue }) => {
		try {
			const res = await fetch(`${BASE}/${kazooId}/guests/paste`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ mode: 'confirm', rows }),
			})
			return await parseJson(res)
		} catch (err) {
			return rejectWithValue({
				message: err.message,
				code: err.code,
				errors: err.errors,
			})
		}
	}
)

export const bulkCreateGuestListPreview = createAsyncThunk(
	'guestList/bulkCreatePreview',
	async ({ kazooId, guests }, { rejectWithValue }) => {
		try {
			const res = await fetch(`${BASE}/${kazooId}/guests/bulk-create`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ mode: 'preview', guests }),
			})
			return await parseJson(res)
		} catch (err) {
			return rejectWithValue({
				message: err.message,
				code: err.code,
				errors: err.errors,
			})
		}
	}
)

export const bulkCreateGuestListConfirm = createAsyncThunk(
	'guestList/bulkCreateConfirm',
	async ({ kazooId, guests }, { rejectWithValue }) => {
		try {
			const res = await fetch(`${BASE}/${kazooId}/guests/bulk-create`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ mode: 'confirm', guests }),
			})
			return await parseJson(res)
		} catch (err) {
			return rejectWithValue({
				message: err.message,
				code: err.code,
				errors: err.errors,
			})
		}
	}
)

const initialState = {
	guests: [],
	total: 0,
	page: 1,
	pageSize: 50,
	includeArchived: false,
	q: '',
	filters: {},
	guestTags: [],
	eventTags: [],
	customFieldDefinitions: [],
	status: 'idle',
	error: null,
	importStatus: 'idle',
}

const guestListSlice = createSlice({
	name: 'guestList',
	initialState,
	reducers: {
		setIncludeArchived(state, action) {
			state.includeArchived = Boolean(action.payload)
		},
		setGuestListQuery(state, action) {
			state.q = action.payload ?? ''
		},
		setGuestListFilters(state, action) {
			state.filters = action.payload ?? {}
		},
		clearGuestListError(state) {
			state.error = null
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchGuestList.pending, (state) => {
				state.status = 'loading'
				state.error = null
			})
			.addCase(fetchGuestList.fulfilled, (state, action) => {
				state.status = 'succeeded'
				state.guests = action.payload.guests ?? []
				state.total = action.payload.total ?? 0
				state.page = action.payload.page ?? 1
				state.pageSize = action.payload.pageSize ?? 50
			})
			.addCase(fetchGuestList.rejected, (state, action) => {
				state.status = 'failed'
				state.error = action.error.message
				toastHelper(action.error.message || 'Failed to load guests', 'error')
			})
			.addCase(searchGuestList.pending, (state) => {
				state.status = 'loading'
			})
			.addCase(searchGuestList.fulfilled, (state, action) => {
				state.status = 'succeeded'
				state.guests = action.payload.guests ?? []
				state.total = action.payload.total ?? 0
				state.page = action.payload.page ?? 1
				state.pageSize = action.payload.pageSize ?? 50
			})
			.addCase(searchGuestList.rejected, (state, action) => {
				state.status = 'failed'
				state.error = action.error.message
				toastHelper(action.error.message || 'Search failed', 'error')
			})
			.addCase(createGuestListGuest.fulfilled, (state, action) => {
				toastHelper('Guest added', 'success')
				if (action.payload.guest) {
					state.guests = [action.payload.guest, ...state.guests]
					state.total += 1
				}
			})
			.addCase(createGuestListGuest.rejected, (_state, action) => {
				const payload = action.payload || {}
				const msg =
					payload.code === 'DUPLICATE_WARNING'
						? 'Possible duplicate guest — choose Create anyway or edit the existing guest'
						: payload.message || 'Failed to add guest'
				toastHelper(msg, 'error')
			})
			.addCase(updateGuestListGuest.fulfilled, (state, action) => {
				toastHelper('Guest updated', 'success')
				const guest = action.payload.guest
				if (!guest) return
				state.guests = state.guests.map((g) =>
					g._id === guest._id ? guest : g
				)
			})
			.addCase(updateGuestListGuest.rejected, (_state, action) => {
				toastHelper(action.error.message || 'Failed to update guest', 'error')
			})
			.addCase(deleteGuestListGuest.fulfilled, (state, action) => {
				toastHelper('Guest deleted', 'success')
				const id = action.payload.deletedGuestId
				state.guests = state.guests.filter((g) => g._id !== id)
				state.total = Math.max(0, state.total - 1)
			})
			.addCase(deleteGuestListGuest.rejected, (_state, action) => {
				toastHelper(action.error.message || 'Failed to delete guest', 'error')
			})
			.addCase(fetchGuestTags.fulfilled, (state, action) => {
				state.guestTags = action.payload.tags ?? []
			})
			.addCase(fetchEventTags.fulfilled, (state, action) => {
				state.eventTags = action.payload.tags ?? []
			})
			.addCase(patchEventTags.fulfilled, (state, action) => {
				state.eventTags = action.payload.tags ?? []
				toastHelper('Event tags updated', 'success')
			})
			.addCase(patchEventTags.rejected, (_state, action) => {
				toastHelper(action.error.message || 'Failed to update event tags', 'error')
			})
			.addCase(fetchCustomFields.fulfilled, (state, action) => {
				state.customFieldDefinitions = action.payload.definitions ?? []
			})
			.addCase(pasteGuestListPreview.pending, (state) => {
				state.importStatus = 'loading'
			})
			.addCase(pasteGuestListPreview.fulfilled, (state) => {
				state.importStatus = 'succeeded'
			})
			.addCase(pasteGuestListPreview.rejected, (state, action) => {
				state.importStatus = 'failed'
				toastHelper(
					action.payload?.message || action.error.message || 'Paste preview failed',
					'error'
				)
			})
			.addCase(pasteGuestListConfirm.pending, (state) => {
				state.importStatus = 'loading'
			})
			.addCase(pasteGuestListConfirm.fulfilled, (state, action) => {
				state.importStatus = 'succeeded'
				const results = action.payload.results ?? []
				const created = results.filter((r) => r.status === 'created' || r.status === 'replaced')
					.length
				const failed = results.filter((r) => r.status === 'failed').length
				const skipped = results.filter((r) => r.status === 'skipped').length
				const parts = [`Imported ${created} guest${created === 1 ? '' : 's'}`]
				if (skipped) parts.push(`${skipped} skipped`)
				if (failed) parts.push(`${failed} failed`)
				toastHelper(parts.join(' · '), created ? 'success' : 'error')
			})
			.addCase(pasteGuestListConfirm.rejected, (state, action) => {
				state.importStatus = 'failed'
				toastHelper(
					action.payload?.message || action.error.message || 'Import failed',
					'error'
				)
			})
			.addCase(bulkCreateGuestListPreview.pending, (state) => {
				state.importStatus = 'loading'
			})
			.addCase(bulkCreateGuestListPreview.fulfilled, (state) => {
				state.importStatus = 'succeeded'
			})
			.addCase(bulkCreateGuestListPreview.rejected, (state, action) => {
				state.importStatus = 'failed'
				toastHelper(
					action.payload?.message || action.error.message || 'Bulk preview failed',
					'error'
				)
			})
			.addCase(bulkCreateGuestListConfirm.pending, (state) => {
				state.importStatus = 'loading'
			})
			.addCase(bulkCreateGuestListConfirm.fulfilled, (state, action) => {
				state.importStatus = 'succeeded'
				const results = action.payload.results ?? []
				const created = results.filter(
					(r) => r.status === 'created' || r.status === 'replaced'
				).length
				toastHelper(
					`Imported ${created} guest${created === 1 ? '' : 's'}`,
					created ? 'success' : 'error'
				)
			})
			.addCase(bulkCreateGuestListConfirm.rejected, (state, action) => {
				state.importStatus = 'failed'
				toastHelper(
					action.payload?.message || action.error.message || 'Bulk import failed',
					'error'
				)
			})
	},
})

export const {
	setIncludeArchived,
	setGuestListQuery,
	setGuestListFilters,
	clearGuestListError,
} = guestListSlice.actions

export default guestListSlice.reducer
